import { PLAN_STATE, type PlanState, UserRoles } from "@next-platform/client-common-core";
import { IconVariants } from "@next-platform/client-common-spicy-ui";
import type { UserRole } from "@next-platform/packages-ts-types";

export type PlanColorTokens = {
  icon: IconVariants;
  text: string;
  statusText?: string;
  background: string;
  border: string;
  bar: string;
  heroFrom: string;
  heroVia: string;
  iconBadgeFrom: string;
  iconBadgeTo: string;
};

type TierRole = typeof UserRoles.PREMIUM | typeof UserRoles.BASIC | typeof UserRoles.MINIMAL | typeof UserRoles.FREE;

const PLAN_TIER_COLORS = {
  [UserRoles.FREE]: {
    icon: IconVariants.SPARKLE,
    text: "text-gray-11",
    background: "bg-gray-5/30",
    border: "border-gray-8/40",
    bar: "bg-gray-8",
    heroFrom: "from-gray-6/25",
    heroVia: "via-gray-6/10",
    iconBadgeFrom: "from-gray-6",
    iconBadgeTo: "to-gray-5",
  },
  [UserRoles.MINIMAL]: {
    icon: IconVariants.COFFEE,
    text: "text-blue-10",
    background: "bg-blue-9/10",
    border: "border-blue-10/60",
    bar: "bg-blue-10",
    heroFrom: "from-blue-2",
    heroVia: "via-blue-2/50",
    iconBadgeFrom: "from-blue-10",
    iconBadgeTo: "to-blue-9",
  },
  [UserRoles.BASIC]: {
    icon: IconVariants.FAVORITE,
    text: "text-purple-10",
    background: "bg-purple-9/10",
    border: "border-purple-9/40",
    bar: "bg-purple-9",
    heroFrom: "from-purple-2",
    heroVia: "via-purple-2/50",
    iconBadgeFrom: "from-purple-9",
    iconBadgeTo: "to-purple-8",
  },
  [UserRoles.PREMIUM]: {
    icon: IconVariants.GEM,
    text: "text-yellow-10",
    background: "bg-yellow-9/10",
    border: "border-yellow-10/40",
    bar: "bg-yellow-9",
    heroFrom: "from-yellow-3/70",
    heroVia: "via-yellow-3/50",
    iconBadgeFrom: "from-yellow-10",
    iconBadgeTo: "to-yellow-8 to-60%",
  },
} as const satisfies Record<TierRole, PlanColorTokens>;

type PlanStateColorConfig = Omit<PlanColorTokens, "icon">;
type PlanStateColorOverrides = Partial<PlanStateColorConfig>;

const EXPIRED_PLAN_COLORS = {
  text: "text-red-10",
  statusText: "text-red-10",
  background: "bg-red-9/10",
  border: "border-red-10",
  bar: "bg-red-9",
  heroFrom: "from-red-9/10",
  heroVia: "via-red-9/5",
  iconBadgeFrom: "bg-gray-5",
  iconBadgeTo: "border-gray-8",
} satisfies PlanStateColorConfig;

const GRACE_PLAN_COLOR_OVERRIDES = {
  ...EXPIRED_PLAN_COLORS,
  statusText: "text-red-9",
} satisfies PlanStateColorOverrides;

const resolveTierRole = (role: UserRole): TierRole => {
  if (role in PLAN_TIER_COLORS) {
    return role as TierRole;
  }

  return UserRoles.FREE;
};

const getPlanTierColors = (role: UserRole): PlanColorTokens => {
  return PLAN_TIER_COLORS[resolveTierRole(role)];
};

export const getPlanDisplayColors = ({ role, planState }: { role: UserRole; planState: PlanState }): PlanColorTokens => {
  const tierColors = getPlanTierColors(role);

  if (planState === PLAN_STATE.GRACE) {
    return { ...tierColors, ...GRACE_PLAN_COLOR_OVERRIDES, iconBadgeFrom: tierColors.iconBadgeFrom, iconBadgeTo: tierColors.iconBadgeTo };
  }

  if (planState === PLAN_STATE.EXPIRED) {
    return { ...EXPIRED_PLAN_COLORS, icon: tierColors.icon };
  }

  return tierColors;
};

export const resolveStatusTextColor = (colors: PlanColorTokens): string => colors.statusText ?? colors.text;

export const isExpiredPlanState = (planState: PlanState): boolean => planState === PLAN_STATE.EXPIRED;
